import { useClerk } from '@clerk/react-router';
import { useEffect } from 'react';
import { isOnHlxHost } from '~/config/branding';
import { authDebug, summarizeUrl } from '~/routing/authDebug';
import {
  buildSatelliteAuthCompleteUrl,
  RETURN_TO_PARAM,
  sanitizeReturnTo,
} from '~/routing/returnTo';
import { paths } from '~/routing/routes';
import { isSatelliteSyncBlocked, recordSatelliteSyncAttempt } from './satelliteSyncGuard';

const withAuthPath = (url: string, path: string): string => {
  const parsed = new URL(url, window.location.origin);
  parsed.pathname = path;
  return parsed.toString();
};

// Markers Clerk adds while handing the browser back from the primary.
// Production uses `__clerk_handshake`; development (pk_test) instances use
// `__clerk_synced` + a `__clerk_db_jwt` dev-browser token. When one is present
// clerk-js must mount and consume it, so we must NOT bounce back to the primary
// — doing so is what sustained the MCV-1445 loop on non-prod envs where only
// `__clerk_handshake` was guarded.
const CLERK_SYNC_MARKERS = ['__clerk_handshake', '__clerk_synced', '__clerk_db_jwt'] as const;

// These must be read as real top-level parameters, never substring-matched
// against the href: a URL like `/sign-in?return_to=%2F..%3F__clerk_synced%3Dfalse`
// embeds the marker name inside an encoded `return_to` value and would otherwise
// false-positive — suppressing the redirect and (illegally) rendering Clerk auth
// on the satellite, and bypassing the attempt breaker. Clerk places these in the
// query; its hash-routing flows can also carry them in `#/?...`, so check both.
const isClerkSyncInProgress = (href: string): boolean => {
  let url: URL;
  try {
    url = new URL(href);
  } catch {
    return false;
  }
  const hashQuery = url.hash.includes('?')
    ? new URLSearchParams(url.hash.slice(url.hash.indexOf('?') + 1))
    : null;
  return CLERK_SYNC_MARKERS.some(
    (key) => url.searchParams.has(key) || (hashQuery?.has(key) ?? false)
  );
};

export type SatelliteRedirectState = {
  /** True while bouncing the browser to the primary — caller should render null. */
  redirecting: boolean;
  /**
   * True once the satellite session has failed to settle after repeated
   * bounces (e.g. Safari/ITP blocking the cross-site cookie). The caller should
   * stop and render a recoverable message instead of looping forever.
   */
  blocked: boolean;
};

export type SatelliteRedirectOptions = {
  /**
   * Where to land once auth completes, when the caller knows better than this
   * hook's defaults.
   *
   * [MCV-1588] Only the page knows that "employer sign-up" carrying an
   * `invitationCode` actually means "join an existing organization", and its
   * destination is the invitation accept page rather than the registration
   * wizard. On a satellite host this hook short-circuits the page before it
   * renders `<SignUp>`, so the `authHome` the page computed never reaches
   * Clerk — passing it in is what carries the invitation across the hop.
   *
   * An explicit `return_to` in the URL still wins, and the value is sanitised
   * exactly like one, so a caller cannot widen where the app will redirect to.
   */
  returnTo?: string | null;
};

// Clerk forbids <SignIn />, <SignUp />, and useSignUp/useSignIn from operating
// on a satellite domain — auth must run on the primary. This hook detects
// satellite mode, redirects to the primary's sign-in with a redirect_url so
// Clerk's handshake brings the user back here after auth, and reports whether
// the caller should bail out of rendering its Clerk components.
export const useSatelliteRedirect = (
  options?: SatelliteRedirectOptions
): SatelliteRedirectState => {
  const clerk = useClerk();
  // Read to a primitive: an inline options object is a new reference each
  // render and would re-run the effect (and its attempt counter) forever.
  const requestedReturnTo = options?.returnTo ?? null;
  const satellite = isOnHlxHost(window.location.hostname);
  const syncInProgress = isClerkSyncInProgress(window.location.href);
  // Read once for render; the effect re-reads after recording its own attempt.
  const blocked = satellite && !syncInProgress && isSatelliteSyncBlocked();

  useEffect(() => {
    if (!satellite) return;
    // Let Clerk process the return token instead of bouncing to primary again.
    if (syncInProgress) {
      authDebug('satellite.redirect.skipped_sync_in_progress');
      return;
    }

    // Circuit breaker: the satellite session keeps failing to establish (the
    // cross-site cookie is being blocked). Stop bouncing so the caller can show
    // a recoverable message rather than thrashing between hosts forever.
    if (isSatelliteSyncBlocked()) {
      authDebug('satellite.redirect.blocked', {
        reason: 'max_sync_attempts_reached',
      });
      return;
    }

    // Map the satellite path to the corresponding primary path so a user who
    // wanted to sign up doesn't land on the sign-in page (and vice versa).
    const path = window.location.pathname;
    const targetPath = path.startsWith(paths.publicAuth.signUpEmployer)
      ? paths.publicAuth.signUpEmployer
      : path.startsWith('/signUp')
        ? paths.publicAuth.signUpProvider
        : path.startsWith(paths.publicAuth.signUp)
          ? paths.publicAuth.signUp
          : paths.publicAuth.signIn;
    const origin = `${window.location.protocol}//${window.location.host}`;
    const searchParams = new URLSearchParams(window.location.search);
    // An explicit `return_to` in the URL wins; the caller's destination is the
    // fallback ahead of the role defaults. Both go through the same sanitiser.
    const returnTo =
      sanitizeReturnTo(searchParams.get(RETURN_TO_PARAM), origin) ??
      sanitizeReturnTo(requestedReturnTo, origin);
    // Authentication does not choose a role. AuthComplete resolves an existing
    // account or sends a new identity through the explicit role-selection gate.
    const defaultReturnTo =
      targetPath === paths.publicAuth.signIn
        ? paths.hlx.jobOpportunities
        : paths.register.roleSelect;
    // Return to the intended satellite app page, not the satellite auth page.
    // Clerk's URL builders add the sync trigger required for multi-domain
    // satellite sessions; hardcoded primary URLs can return without a settled
    // HLX session.
    const redirectUrl = buildSatelliteAuthCompleteUrl(origin, returnTo ?? defaultReturnTo);
    const redirectWithoutAppReturn =
      targetPath === paths.publicAuth.signIn
        ? clerk.buildSignInUrl({ redirectUrl })
        : withAuthPath(clerk.buildSignUpUrl({ redirectUrl }), targetPath);
    const attempt = recordSatelliteSyncAttempt();
    authDebug('satellite.redirect.replace', {
      targetPath,
      returnTo,
      attempt,
      redirectUrl: summarizeUrl(redirectUrl),
      redirect: summarizeUrl(redirectWithoutAppReturn),
    });
    window.location.replace(redirectWithoutAppReturn);
  }, [clerk, satellite, syncInProgress, requestedReturnTo]);

  // Don't bail render during a sync hand-off — Clerk needs to mount and process
  // the token. Bailing here would leave the page permanently blank if a user
  // ever lands on /sign-in?__clerk_handshake=... on the satellite.
  return {
    redirecting: satellite && !syncInProgress && !blocked,
    blocked,
  };
};
